import {Dates, useDateTimeConfig, useUserLanguage} from '@smartmixin/core';
import React, {PropsWithChildren} from 'react';
import {IntlProvider} from 'react-intl';
import {attemptChunkReload} from '../lib/chunkReload';
import {loadMessages, onError} from './loadMessages';
import {AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE} from './setup';

const LanguageProvider = ({children}: PropsWithChildren) => {
  const language = useUserLanguage();
  const {month_format: monthFormat} = useDateTimeConfig();

  // user preferred language
  const userProfileLanguage = AVAILABLE_LANGUAGES.includes(language)
    ? language
    : null;

  const locale = userProfileLanguage || DEFAULT_LANGUAGE;

  // With the date order: English long dates follow it (FE #430).
  Dates.setLocale(locale, monthFormat);
  Dates.setTimeDifferenceThresholds();

  const [messagesForLocale, setMessagesForLocale] =
    React.useState<Record<string, string>>();

  React.useEffect(() => {
    loadMessages(locale)
      .then((messages) => {
        setMessagesForLocale(messages);
      })
      .catch(() => {
        // Stale chunk after deployment: the cached app references a translation
        // chunk hash that no longer exists on the server. A guarded hard reload
        // pulls fresh assets. Shared with the route-level RouteErrorBoundary via
        // lib/chunkReload, which adds the anti-loop guard a bare reload lacks.
        attemptChunkReload();
      });
  }, [locale]);

  return messagesForLocale ? (
    <IntlProvider
      locale={locale}
      messages={messagesForLocale}
      onError={onError}>
      {children}
    </IntlProvider>
  ) : null;
};

export default LanguageProvider;
